"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { Check } from "lucide-react";

/**
 * The same password rules that mirror the Zod schema in RegisterForm:
 *   .min(8)
 *   .refine(v => /[a-z]/.test(v))
 *   .refine(v => /[A-Z]/.test(v))
 *   .refine(v => /[0-9]/.test(v))
 *   .refine(v => /[^a-zA-Z0-9]/.test(v))
 *
 * Keeping them here (single source of truth for the checklist) avoids
 * duplicating the Zod schema while still letting us show per-rule feedback.
 */
export const PASSWORD_RULES = [
  { key: "min_length", test: (v: string) => v.length >= 8 },
  { key: "uppercase",  test: (v: string) => /[A-Z]/.test(v) },
  { key: "lowercase",  test: (v: string) => /[a-z]/.test(v) },
  { key: "number",     test: (v: string) => /[0-9]/.test(v) },
  { key: "special",    test: (v: string) => /[^a-zA-Z0-9]/.test(v) },
] as const;

interface Props {
  /** The current raw value of the password field */
  password: string;
}

export default function PasswordRequirements({ password }: Props) {
  const t = useTranslations("register.password_requirements");

  const results = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ key: rule.key, met: rule.test(password) })),
    [password]
  );

  return (
    <div className="mt-2 space-y-1" aria-live="polite" aria-label={t("title")}>
      <p className="text-xs font-medium text-text-secondary mb-1.5">{t("title")}</p>
      <ul className="space-y-1">
        {results.map(({ key, met }) => (
          <li
            key={key}
            className={`flex items-center gap-1.5 text-xs transition-colors duration-200 ${
              met ? "text-success" : "text-text-muted"
            }`}
          >
            <span
              className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border transition-all duration-200 shrink-0 ${
                met
                  ? "border-success bg-success text-on-success"
                  : "border-text-muted bg-transparent"
              }`}
              aria-hidden="true"
            >
              {met && <Check size={9} strokeWidth={3} />}
            </span>
            <span>{t(key)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
